import React from 'react';
import {
  DecoratorNode,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';

import { legalEntityClasses } from '../config/calendarTheme';

/**
 * Document reference node data structure
 */
export interface DocumentReferenceData {
  documentId: string;
  displayName: string;
  documentType: 'contract' | 'motion' | 'brief' | 'evidence' | 'correspondence' | 'other';
  fileType: 'pdf' | 'doc' | 'docx' | 'txt' | 'other';
}

/**
 * Serialized document reference node for JSON storage
 */
export interface SerializedDocumentReferenceNode extends Spread<
  {
    documentData: DocumentReferenceData;
    type: 'document-reference';
    version: 1;
  },
  SerializedLexicalNode
> {}

/**
 * Custom Lexical node for /doc references
 */
export class DocumentReferenceNode extends DecoratorNode<React.JSX.Element> {
  __documentData: DocumentReferenceData;

  static getType(): string {
    return 'document-reference';
  }

  static clone(node: DocumentReferenceNode): DocumentReferenceNode {
    return new DocumentReferenceNode(node.__documentData, node.__key);
  }

  constructor(documentData: DocumentReferenceData, key?: NodeKey) {
    super(key);
    this.__documentData = documentData;
  }

  exportJSON(): SerializedDocumentReferenceNode {
    return {
      documentData: this.__documentData,
      type: 'document-reference',
      version: 1,
    };
  }

  static importJSON(serializedNode: SerializedDocumentReferenceNode): DocumentReferenceNode {
    const { documentData } = serializedNode;
    return $createDocumentReferenceNode(documentData);
  }

  createDOM(): HTMLElement {
    const span = document.createElement('span');
    span.className = legalEntityClasses.document;
    return span;
  }

  updateDOM(): false {
    return false;
  }

  decorate(): React.JSX.Element {
    return (
      <span
        className={legalEntityClasses.document}
        title={`Document: ${this.__documentData.displayName}`}
      >
        📄 /doc/{this.__documentData.displayName}
      </span>
    );
  }

  getTextContent(): string {
    return `/doc/${this.__documentData.displayName}`;
  }

  isImmutable(): boolean {
    return true;
  }

  isKeyboardSelectable(): boolean {
    return true;
  }
}

export function $createDocumentReferenceNode(documentData: DocumentReferenceData): DocumentReferenceNode {
  return new DocumentReferenceNode(documentData);
}

export function $isDocumentReferenceNode(node: LexicalNode | null | undefined): node is DocumentReferenceNode {
  return node instanceof DocumentReferenceNode;
} 